import * as React from "react";
import { loadStripe, type Stripe } from "@stripe/stripe-js";
import { EmbeddedCheckout, EmbeddedCheckoutProvider } from "@stripe/react-stripe-js";

/**
 * Stripe's card form, mounted INSIDE our page rather than redirecting to
 * stripe.com.
 *
 * The fields live in a Stripe-hosted iframe, so card details go straight to
 * Stripe and never touch our servers — PCI scope is the same SAQ-A as the
 * redirect flow was. That's also why Stripe.js is loaded from js.stripe.com at
 * runtime instead of being bundled: self-hosting it breaks PCI compliance.
 *
 * On completion Stripe redirects the whole page to the session's `return_url`
 * (`/pay/:ref?paid=1`), which polls until the WEBHOOK marks the enrolment
 * paid. As with the redirect flow, the return hop is never treated as proof of
 * payment — only the webhook is.
 */

/** One Stripe.js load per publishable key, shared across mounts. Calling
 *  loadStripe on every render would refetch the script each time. */
const stripeCache = new Map<string, Promise<Stripe | null>>();

function stripeFor(publishableKey: string): Promise<Stripe | null> {
  let p = stripeCache.get(publishableKey);
  if (!p) {
    p = loadStripe(publishableKey);
    stripeCache.set(publishableKey, p);
  }
  return p;
}

export function CardCheckout({
  publishableKey,
  fetchClientSecret,
  onError,
}: {
  /** From GET /api/payment-config. Null when card payments are off. */
  publishableKey: string | null;
  /** Creates the Checkout Session server-side and resolves its client_secret.
   *  Stripe calls this itself, so the session is only created once the form is
   *  actually being shown. */
  fetchClientSecret: () => Promise<string>;
  onError?: (message: string) => void;
}) {
  const [failed, setFailed] = React.useState<string | null>(null);

  const stripePromise = React.useMemo(
    () => (publishableKey ? stripeFor(publishableKey) : null),
    [publishableKey],
  );

  /* Stripe.js is a third-party script: an ad blocker, an offline moment or a
   * strict network can stop it loading. Say so plainly and point at another
   * payment method rather than leaving an empty box. */
  React.useEffect(() => {
    if (!stripePromise) return;
    let cancelled = false;
    void stripePromise
      .then((s) => {
        if (!cancelled && !s) setFailed("We couldn't load the secure card form.");
      })
      .catch(() => {
        if (!cancelled) setFailed("We couldn't load the secure card form.");
      });
    return () => {
      cancelled = true;
    };
  }, [stripePromise]);

  const wrappedFetch = React.useCallback(async () => {
    try {
      return await fetchClientSecret();
    } catch (err) {
      const message = "We couldn't start the card payment — please try again.";
      setFailed(message);
      onError?.(message);
      throw err;
    }
  }, [fetchClientSecret, onError]);

  if (!publishableKey) return null;

  if (failed) {
    return (
      <p role="alert" className="rounded-card border border-border bg-paper p-4 text-sm text-muted">
        {failed} Please try again, or choose another payment method above.
      </p>
    );
  }

  return (
    <div className="rounded-card border border-border bg-card p-2">
      <EmbeddedCheckoutProvider
        stripe={stripePromise}
        options={{ fetchClientSecret: wrappedFetch }}
      >
        <EmbeddedCheckout />
      </EmbeddedCheckoutProvider>
    </div>
  );
}
